import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth, useCart } from "../App";
import { Button } from "../components/ui/button";
import { ArrowLeft, ShoppingBag, Minus, Plus } from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProduct(response.data);
      } catch (error) {
        console.error("Failed to fetch product:", error);
        navigate("/shop");
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, token, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <Skeleton className="aspect-square w-full bg-zinc-800" />
        <div className="p-4 space-y-4">
          <Skeleton className="h-6 w-3/4 bg-zinc-800" />
          <Skeleton className="h-8 w-1/3 bg-zinc-800" />
          <Skeleton className="h-20 w-full bg-zinc-800" />
        </div>
      </div>
    );
  }

  if (!product) return null;

  const hasDiscount = product.discount_price && product.discount_price < product.price;
  const discountPercent = hasDiscount
    ? Math.round((1 - product.discount_price / product.price) * 100)
    : 0;

  const handleAddToCart = () => {
    addToCart(product, quantity);
  };

  return (
    <div className="min-h-screen bg-black" data-testid="product-detail-page">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="fixed top-16 left-4 z-30 w-10 h-10 rounded-full bg-black/80 backdrop-blur-md flex items-center justify-center text-white"
        data-testid="back-button"
      >
        <ArrowLeft size={20} />
      </button>

      {/* Images */}
      <div className="relative">
        <div className="aspect-square bg-zinc-900 overflow-hidden">
          <img
            src={product.images?.[selectedImage] || "https://images.unsplash.com/photo-1760072513403-d70003481414?w=800"}
            alt={product.name}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Badges */}
        {hasDiscount && (
          <div className="absolute top-4 right-4 bg-emerald-500 text-black text-sm font-bold px-3 py-1 rounded-full">
            -{discountPercent}%
          </div>
        )}

        {/* Image Thumbnails */}
        {product.images?.length > 1 && (
          <div className="flex gap-2 p-4 overflow-x-auto">
            {product.images.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setSelectedImage(idx)}
                className={`w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-all ${
                  selectedImage === idx ? "border-emerald-500" : "border-transparent"
                }`}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        <p className="text-xs text-white/50 uppercase tracking-wider">{product.category}</p>
        <h1 className="text-xl font-light text-white">{product.name}</h1>

        {/* Price */}
        <div className="flex items-center gap-3">
          {hasDiscount ? (
            <>
              <span className="text-2xl font-bold text-emerald-500">€{product.discount_price.toFixed(2)}</span>
              <span className="text-lg text-white/40 line-through">€{product.price.toFixed(2)}</span>
            </>
          ) : (
            <span className="text-2xl font-bold text-white">€{product.price.toFixed(2)}</span>
          )}
        </div>

        {/* Description */}
        <p className="text-sm text-white/70 leading-relaxed">{product.description}</p>

        {/* Availability */}
        {product.is_available ? (
          <p className="text-sm text-emerald-500">✓ Disponibile ({product.stock} in magazzino)</p>
        ) : (
          <p className="text-sm text-red-400">✗ Non disponibile</p>
        )}

        {/* Quantity Selector */}
        {product.is_available && (
          <div className="flex items-center gap-4">
            <span className="text-sm text-white/70">Quantità:</span>
            <div className="flex items-center bg-zinc-900 rounded-full">
              <button
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="w-10 h-10 flex items-center justify-center text-white hover:text-emerald-500 transition-colors"
                data-testid="quantity-minus"
              >
                <Minus size={18} />
              </button>
              <span className="w-10 text-center text-white font-medium">{quantity}</span>
              <button
                onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                className="w-10 h-10 flex items-center justify-center text-white hover:text-emerald-500 transition-colors"
                data-testid="quantity-plus"
              >
                <Plus size={18} />
              </button>
            </div>
          </div>
        )}

        {/* Add to Cart Button */}
        <Button
          onClick={handleAddToCart}
          disabled={!product.is_available}
          className="w-full h-14 bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-full text-lg"
          data-testid="add-to-cart-btn"
        >
          <ShoppingBag className="mr-2" size={20} />
          {product.is_available ? "Aggiungi al Carrello" : "Non Disponibile"}
        </Button>
      </div>
    </div>
  );
};

export default ProductDetailPage;
